<script setup lang="ts">
import * as echarts from 'echarts'
import {
  getAnomalyRadar,
  getBrokerHoldings,
  getBuyingList,
  getCapitalFlow,
  getHKMarketOverview,
  getSectorList,
  getShortSellingList,
  getWarrants,
} from '@/api/modules/market'
import card from '../index/components/card.vue'
import HKConnect from './components/HKConnect.vue'
import MainlandMarket from './components/MainlandMarket.vue'

defineOptions({
  name: 'Market',
})
definePage({
  name: 'market',
  meta: {
    title: '行情',
    cache: true,
  },
})

interface ChartDataItem {
  date: string
  holding: number
  index: number
}

interface WarrantItem {
  name: string
  code: string
  price: number
  changeRate: number
}

interface CapitalFlowItem {
  name: string
  code: string
  price: number
  changeRate: number
}

interface ShortSellingItem {
  rank: number
  name: string
  code: string
  value: string
  percent: number
}

interface BuyingItem {
  rank: number
  name: string
  code: string
  value: string
  percent: number
}

interface AnomalyRadarItem {
  name: string
  code: string
  action: string
  volume: string
}

interface SectorItem {
  name: string
  trend: string
}

onActivated(() => {
  console.log(999)
})

const router = useRouter()
const activeTab = ref(0)
const tabs = ['港股', '沪深', '港股通', '中华通']

const hkMarketOverview = ref({
  hsiIndex: { name: '恒生指数', code: 'HSI.HK', price: 8895.12, change: -13.98, changeRate: -0.16 },
  hsceIndex: { name: '恒生国企', code: 'HSCEI.HK', price: 25816.93, change: 44.79, changeRate: 0.10 },
  hstIndex: { name: '恒生科技', code: 'HSTECH.HK', price: 5473.30, change: 10.41, changeRate: 0.33 },
})

const warrants = ref<{
  index: { name: string, code: string, price: number, change: number, changeRate: number }
  list: WarrantItem[]
}>({
  index: { name: '恒生指数', code: 'HSI.HK', price: 25818.93, change: 44.79, changeRate: 0.17 },
  list: [],
})

const capitalFlow = ref<{
  index: { name: string, code: string, price: number, change: number, changeRate: number }
  list: CapitalFlowItem[]
}>({
  index: { name: '恒生指数', code: 'HSI.HK', price: 25818.93, change: 44.79, changeRate: 0.17 },
  list: [],
})

const brokerHoldings = ref<{
  chartData: ChartDataItem[]
  brokerName: string
}>({
  chartData: [],
  brokerName: '孙权夺妻',
})

const shortSellingList = ref<{
  updateTime: string
  list: ShortSellingItem[]
}>({
  updateTime: '',
  list: [],
})

const buyingList = ref<{
  updateTime: string
  list: BuyingItem[]
}>({
  updateTime: '',
  list: [],
})

const anomalyRadar = ref<{
  updateTime: string
  list: AnomalyRadarItem[]
}>({
  updateTime: '',
  list: [],
})

const sectorList = ref<{
  updateTime: string
  list: SectorItem[]
}>({
  updateTime: '',
  list: [],
})

const chartRef = useTemplateRef('chartRef')
let chartInstance: echarts.ECharts | null = null

function formatSignedNumber(val: number, digits = 2) {
  const abs = Math.abs(val).toFixed(digits)
  if (val > 0) {
    return `+${abs}`
  }
  if (val < 0) {
    return `-${abs}`
  }
  return abs
}

function formatNumberNoPlus(val: number, digits = 2) {
  const abs = Math.abs(val).toFixed(digits)
  return val < 0 ? `-${abs}` : abs
}

function getUpDownClass(val: number) {
  return val < 0 ? 'is-down' : 'is-up'
}

function getRankValueClass(val: string) {
  const n = Number(val.replace(/[^\d.-]/g, ''))
  // 榜单里：正值(更高)用红色提示，负值用绿色提示
  return n < 0 ? 'is-up' : 'is-down'
}

function initChart() {
  if (!chartRef.value || !brokerHoldings.value.chartData.length) {
    return
  }

  chartInstance = echarts.init(chartRef.value)

  const dates = brokerHoldings.value.chartData.map(item => item.date)
  const holdings = brokerHoldings.value.chartData.map(item => item.holding)
  const indices = brokerHoldings.value.chartData.map(item => item.index)

  const option = {
    grid: {
      left: '0%',
      right: '0%',
      bottom: '18%',
      top: '5%',
      containLabel: true,
    },
    legend: {
      data: ['持股变化', '恒生指数'],
      bottom: '5%',
      textStyle: {
        fontSize: 12,
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLine: {
        lineStyle: {
          color: '#E4E5E9',
        },
      },
      axisLabel: {
        fontSize: 10,
        color: '#666',
      },
    },
    yAxis: [
      {
        type: 'value',
        name: '%',
        position: 'left',
        axisLine: {
          lineStyle: {
            color: '#E4E5E9',
          },
        },
        axisLabel: {
          fontSize: 10,
          color: '#666',
          formatter: '{value}%',
        },
        splitLine: {
          lineStyle: {
            color: '#F1F1F1',
          },
        },
      },
      {
        type: 'value',
        name: '',
        position: 'right',
        axisLine: {
          lineStyle: {
            color: '#E4E5E9',
          },
        },
        axisLabel: {
          fontSize: 10,
          color: '#666',
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: [
      {
        name: '持股变化',
        type: 'line',
        yAxisIndex: 0,
        data: holdings,
        lineStyle: {
          color: '#ff8c00',
          width: 2,
        },
        itemStyle: {
          color: '#ff8c00',
        },
        symbol: 'circle',
        symbolSize: 4,
      },
      {
        name: '恒生指数',
        type: 'line',
        yAxisIndex: 1,
        data: indices,
        lineStyle: {
          color: '#c5cfdc',
          width: 2,
        },
        itemStyle: {
          color: '#c5cfdc',
        },
        symbol: 'circle',
        symbolSize: 4,
      },
    ],
  }

  chartInstance.setOption(option)
}

function tabChange(val: any) {
  if (val === 0) {
    nextTick(() => {
      initChart()
    })
  }
}

onMounted(async () => {
  try {
    const [
      overviewRes,
      warrantsRes,
      capitalFlowRes,
      brokerRes,
      shortRes,
      buyingRes,
      radarRes,
      sectorRes,
    ] = await Promise.all([
      getHKMarketOverview(),
      getWarrants(),
      getCapitalFlow(),
      getBrokerHoldings(),
      getShortSellingList(),
      getBuyingList(),
      getAnomalyRadar(),
      getSectorList(),
    ])

    if (overviewRes.status === 1) {
      hkMarketOverview.value = overviewRes.data
    }

    if (warrantsRes.status === 1) {
      warrants.value = warrantsRes.data
    }

    if (capitalFlowRes.status === 1) {
      capitalFlow.value = capitalFlowRes.data
    }

    if (brokerRes.status === 1) {
      brokerHoldings.value = brokerRes.data
      nextTick(() => {
        if (!chartInstance) {
          initChart()
        }
      })
    }

    if (shortRes.status === 1) {
      shortSellingList.value = shortRes.data
    }

    if (buyingRes.status === 1) {
      buyingList.value = buyingRes.data
    }

    if (radarRes.status === 1) {
      anomalyRadar.value = radarRes.data
    }

    if (sectorRes.status === 1) {
      sectorList.value = sectorRes.data
    }
  }
  catch (error) {
    console.error('加载行情数据失败:', error)
  }
})

function onSwitchPage(name: any) {
  router.push({ name })
}

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<template>
  <FmPageLayout navbar tabbar :navbar-border="false">
    <div class="mk">
      <div class="mk-tabs">
        <van-tabs
          v-model:active="activeTab"
          :ellipsis="false"
          line-width="18px"
          title-active-color="#111e36"
          title-inactive-color="#8b95a5"
          background="#F1F2F7"
          @change="tabChange"
        >
          <van-tab v-for="(tab, index) in tabs" :key="index" :title="tab" />
          <FmIcon name="tdesign:format-vertical-align-right" class="absolute right-0 top-[50%] translate-[0,-50%]" />
        </van-tabs>
      </div>

      <HKConnect v-if="activeTab === 2 || activeTab === 3" />
      <MainlandMarket v-else-if="activeTab === 1" />

      <template v-else>
        <div class="mk-notice">
          港股行情至少延时15分钟,购买港股行情
        </div>

        <div style="padding: 0 10px;">
          <div class="mk-index">
            <div class="mk-index__item" @click="onSwitchPage('market-detail')">
              <div class="mk-index__name">
                {{ hkMarketOverview.hsiIndex.name }}
              </div>
              <div class="mk-index__price is-down">
                {{ hkMarketOverview.hsiIndex.price }}
              </div>
              <div class="mk-index__sub is-down">
                <span>{{ formatNumberNoPlus(hkMarketOverview.hsiIndex.change) }}</span>
                <span class="mk-index__gap">{{ formatNumberNoPlus(hkMarketOverview.hsiIndex.changeRate) }}%</span>
              </div>
            </div>
            <div class="mk-index__item" @click="onSwitchPage('market-detail')">
              <div class="mk-index__name">
                {{ hkMarketOverview.hsceIndex.name }}
              </div>
              <div class="mk-index__price is-up">
                {{ hkMarketOverview.hsceIndex.price }}
              </div>
              <div class="mk-index__sub is-up">
                <span>{{ formatNumberNoPlus(hkMarketOverview.hsceIndex.change) }}</span>
                <span class="mk-index__gap">{{ formatSignedNumber(hkMarketOverview.hsceIndex.changeRate) }}%</span>
              </div>
            </div>
            <div class="mk-index__item" @click="onSwitchPage('market-detail')">
              <div class="mk-index__name">
                {{ hkMarketOverview.hstIndex.name }}
              </div>
              <div class="mk-index__price is-up">
                {{ hkMarketOverview.hstIndex.price }}
              </div>
              <div class="mk-index__sub is-up">
                <span>{{ formatNumberNoPlus(hkMarketOverview.hstIndex.change) }}</span>
                <span class="mk-index__gap">{{ formatSignedNumber(hkMarketOverview.hstIndex.changeRate) }}%</span>
              </div>
            </div>
          </div>

          <van-row class="mk-grid" :gutter="[10, 10]">
            <card title="轮证" span="12" @click="onSwitchPage('TrialByOrdeal')">
              <div class="mk-body">
                <div class="mk-subtitle">
                  <div class="mk-subtitle__name">
                    {{ warrants.index.name }}({{ warrants.index.code }})
                  </div>
                  <div class="mk-subtitle__line">
                    <span class="mk-subtitle__price" :class="getUpDownClass(warrants.index.changeRate)">{{ warrants.index.price }}</span>
                    <span class="mk-subtitle__meta" :class="getUpDownClass(warrants.index.changeRate)">
                      {{ formatSignedNumber(warrants.index.changeRate) }}% {{ formatSignedNumber(warrants.index.change) }}
                    </span>
                  </div>
                </div>

                <div v-for="(item, index) in warrants.list" :key="index" class="mk-list-row">
                  <div class="mk-list-left">
                    <div class="mk-list-name">
                      {{ item.name }}
                    </div>
                    <div class="mk-list-code">
                      {{ item.code }}
                    </div>
                  </div>
                  <div class="mk-list-right">
                    <div class="mk-list-val" :class="getUpDownClass(item.changeRate)">
                      {{ item.price }}
                    </div>
                    <div class="mk-list-rate" :class="getUpDownClass(item.changeRate)">
                      {{ formatSignedNumber(item.changeRate) }}%
                    </div>
                  </div>
                </div>
                <div class="mk-list-note">
                  显示前三条轮证记录。
                </div>
                <div class="mk-link">
                  查看详情 <span class="mk-link__arrow">›</span>
                </div>
              </div>
            </card>

            <card title="资金流向" span="12" @click="onSwitchPage('money-flow')">
              <div class="mk-body">
                <div class="mk-subtitle">
                  <div class="mk-subtitle__name">
                    {{ capitalFlow.index.name }}({{ capitalFlow.index.code }})
                  </div>
                  <div class="mk-subtitle__line">
                    <span class="mk-subtitle__price" :class="getUpDownClass(capitalFlow.index.changeRate)">{{ capitalFlow.index.price }}</span>
                    <span class="mk-subtitle__meta" :class="getUpDownClass(capitalFlow.index.changeRate)">
                      {{ formatSignedNumber(capitalFlow.index.changeRate) }}% {{ formatSignedNumber(capitalFlow.index.change) }}
                    </span>
                  </div>
                </div>

                <div v-for="(item, index) in capitalFlow.list" :key="index" class="mk-list-row">
                  <div class="mk-list-left">
                    <div class="mk-list-name">
                      {{ item.name }}
                    </div>
                    <div class="mk-list-code">
                      {{ item.code }}
                    </div>
                  </div>
                  <div class="mk-list-right">
                    <div class="mk-list-val" :class="getUpDownClass(item.changeRate)">
                      {{ item.price }}
                    </div>
                    <div class="mk-list-rate" :class="getUpDownClass(item.changeRate)">
                      {{ formatSignedNumber(item.changeRate) }}%
                    </div>
                  </div>
                </div>
                <div class="mk-list-note">
                  显示前三条轮证记录。
                </div>
                <div class="mk-link">
                  查看详情 <span class="mk-link__arrow">›</span>
                </div>
              </div>
            </card>

            <card title="经纪商持股" span="24" @click="onSwitchPage('market-hold')">
              <div class="mk-body mk-body--chart">
                <div class="mk-row">
                  <div class="mk-row__left">
                    <div class="mk-row__label">
                      持股变化
                      <FmIcon name="ri:share-forward-box-line" class="text-10px color-[#666]" />
                    </div>
                    <div class="mk-row__time">
                      更新: {{ brokerHoldings.chartData.at(-1)?.date }}
                    </div>
                  </div>
                  <div class="mk-row__right" @click.stop="onSwitchPage('market-hold-list')">
                    券商列表 <span class="mk-link__arrow">›</span>
                  </div>
                </div>
                <div ref="chartRef" class="mk-chart" />
              </div>
            </card>

            <card span="24">
              <template #header>
                <van-col :span="24">
                  <div class="mk-section">
                    <div class="mk-section__title">
                      资金行为
                    </div>
                    <div class="mk-section__tabs">
                      沽空榜单&nbsp;&nbsp;扫货榜&nbsp;&nbsp;便捷榜
                    </div>
                  </div>
                </van-col>
              </template>

              <van-row class="mk-card-row" :gutter="[10, 10]">
                <card title="沽空榜单" span="12">
                  <div class="mk-body">
                    <div class="mk-mini-head">
                      更新: {{ shortSellingList.updateTime }}
                    </div>
                    <div v-for="(item, index) in shortSellingList.list" :key="index" class="mk-rank-row">
                      <div class="mk-rank">
                        <span class="mk-rank__tag" :class="`mk-rank__tag--${index + 1}`">TOP{{ item.rank }}</span>
                        <div class="mk-rank__info">
                          <div class="mk-rank__name">
                            {{ item.name }}
                          </div>
                          <div class="mk-rank__code">
                            {{ item.code }}
                          </div>
                        </div>
                      </div>
                      <div class="mk-rank__right">
                        <div class="mk-rank__value" :class="getRankValueClass(item.value)">
                          {{ item.value }}
                        </div>
                        <div class="mk-rank__pct" :class="getRankValueClass(item.value)">
                          {{ getRankValueClass(item.value) !== 'is-up' ? '+' : '-' }}{{ item.percent }}%
                        </div>
                      </div>
                    </div>
                  </div>
                </card>

                <card title="扫货榜" span="12" @click="onSwitchPage('buyer')">
                  <div class="mk-body">
                    <div class="mk-mini-head">
                      更新: {{ buyingList.updateTime }}
                    </div>
                    <div v-for="(item, index) in buyingList.list" :key="index" class="mk-rank-row">
                      <div class="mk-rank">
                        <span class="mk-rank__tag" :class="`mk-rank__tag--${index + 1}`">TOP{{ item.rank }}</span>
                        <div class="mk-rank__info">
                          <div class="mk-rank__name">
                            {{ item.name }}
                          </div>
                          <div class="mk-rank__code">
                            {{ item.code }}
                          </div>
                        </div>
                      </div>
                      <div class="mk-rank__right">
                        <div class="mk-rank__value" :class="getRankValueClass(item.value)">
                          {{ item.value }}
                        </div>
                        <div class="mk-rank__pct" :class="getRankValueClass(item.value)">
                          {{ formatSignedNumber(item.percent) }}%
                        </div>
                      </div>
                    </div>
                  </div>
                </card>
              </van-row>
            </card>

            <card span="24">
              <template #header>
                <van-col :span="24">
                  <div class="mk-section mk-section--mt">
                    <div class="mk-section__title">
                      市场发现
                    </div>
                    <div class="mk-section__tabs">
                      异动雷达&nbsp;&nbsp;行业板块&nbsp;&nbsp;股票排行
                    </div>
                  </div>
                </van-col>
              </template>

              <van-row class="mk-card-row" :gutter="[16, 10]">
                <card title="异动雷达" span="12" @click="onSwitchPage('market-radar')">
                  <div class="mk-body">
                    <div class="mk-mini-head">
                      更新: {{ anomalyRadar.updateTime }}
                    </div>
                    <div v-for="(item, index) in anomalyRadar.list" :key="index" class="mk-radar-row">
                      <div class="mk-radar-left">
                        <div class="mk-radar-name">
                          {{ item.name }}
                        </div>
                        <div class="mk-radar-code">
                          {{ item.code }}
                        </div>
                      </div>
                      <div class="mk-radar-right">
                        <div class="mk-pill">
                          {{ item.action }}
                        </div>
                        <div class="mk-radar-vol">
                          {{ item.volume }}
                        </div>
                      </div>
                    </div>
                  </div>
                </card>

                <card title="行业板块" span="12" @click="onSwitchPage('industry-sector')">
                  <div class="mk-body">
                    <div class="mk-mini-head">
                      更新: {{ sectorList.updateTime }}
                    </div>
                    <div v-for="(item, index) in sectorList.list" :key="index" class="mk-sector-row">
                      <span class="mk-sector-dot" />
                      <span class="mk-sector-name">{{ item.name }}</span>
                      <span class="mk-sector-bar" />
                    </div>
                  </div>
                </card>
              </van-row>
            </card>
          </van-row>
        </div>
      </template>
    </div>
  </FmPageLayout>
</template>

<style lang="scss" scoped>
.mk {
  width: 100%;
  min-height: 100%;
  overflow-x: hidden;
  color: #333;
  background-color: #f1f2f7;
}

.is-up {
  color: #22a875;
}

.is-down {
  color: #e51a2d;
}

.mk-tabs {
  position: relative;
  max-width: 100vw;
  background-color: #f1f2f7;

  :deep(.van-tabs__wrap) {
    height: 44px;
  }

  :deep(.van-tabs__nav) {
    background-color: #f1f2f7;
  }

  :deep(.van-tab) {
    font-size: 15px;
  }

  :deep(.van-tab--active) {
    font-size: 16px;
    font-weight: 600;
  }

  :deep(.van-tabs__line) {
    background-color: #3c78ff;
  }
}

.mk-tabs__icon {
  position: absolute;
  top: 50%;
  right: 10px;
  font-size: 18px;
  color: #111e36;
  transform: translateY(-50%);
}

.mk-notice {
  padding: 8px 10px;
  margin: 0 0 10px;
  font-size: 11px;
  line-height: 16px;
  color: #3c78ff;
  text-align: center;
  background-color: #ffefe5;
  // border-radius: 8px;
}

.mk-index {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.mk-index__item {
  padding: 12px 10px;
  text-align: center;
  background: linear-gradient(180deg, #fff 0%, rgb(255 255 255 / 0%) 100%);
  border-radius: 8px;
}

.mk-index__name {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.mk-index__price {
  margin-bottom: 4px;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
}

.mk-index__sub {
  font-size: 11px;
  font-weight: 500;
}

.mk-index__gap {
  margin-left: 6px;
}

.mk-grid {
  padding-bottom: 4px;
}

.mk-card-row {
  box-sizing: border-box;
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  max-width: 100%;
  padding: 16px;
  padding-top: 5px;
  overflow: auto hidden;
  -webkit-overflow-scrolling: touch;

  :deep(.van-col) {
    flex: 0 0 240px;
    width: 240px;
    min-width: 240px;
    max-width: 240px;
  }

  :deep(.card-conten) {
    box-sizing: border-box;
    width: 240px;
    min-width: 240px;
    max-width: 240px;
    box-shadow: 0 4px 7px 0 rgb(191 206 229 / 50%);
  }
}

.mk-body {
  padding: 0 14px 14px;
}

.mk-body--chart {
  padding-bottom: 10px;
}

.mk-subtitle {
  padding-top: 6px;
  margin-bottom: 10px;
}

.mk-subtitle__name {
  font-size: 12px;
  font-weight: 600;
  color: #111e36;
}

.mk-subtitle__line {
  display: flex;
  flex-direction: column;
  align-items: baseline;
}

.mk-subtitle__price {
  font-size: 20px;
  font-weight: 700;
}

.mk-subtitle__meta {
  font-size: 11px;
  font-weight: 500;
}

.mk-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}

.mk-list-name {
  font-size: 11px;
  font-weight: 500;
  color: #111e36;
}

.mk-list-code {
  margin-top: 2px;
  font-size: 10px;
  color: #8b95a5;
}

.mk-list-right {
  text-align: right;
}

.mk-list-val {
  font-size: 11px;
  font-weight: 600;
}

.mk-list-note {
  font-size: 8px;
  color: #999;
}

.mk-list-rate {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 600;
}

.mk-link {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #3c78ff;
  text-align: center;
}

.mk-link__arrow {
  font-size: 16px;
  line-height: 0;
  vertical-align: -1px;
}

.mk-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.mk-row__label {
  font-size: 13px;
  font-weight: 600;
  color: #111e36;
}

.mk-row__time {
  font-size: 10px;
  color: #8b95a5;
}

.mk-row__right {
  font-size: 12px;
  font-weight: 600;
  color: #3c78ff;
}

.mk-chart {
  width: 100%;
  height: 160px;
}

.mk-section {
  display: flex;
  align-items: baseline;
  // justify-content: space-between;
  padding: 4px 2px 0;
}

.mk-section--mt {
  margin-top: 4px;
}

.mk-section__title {
  margin-right: 7px;
  font-size: 20px;
  font-weight: 700;
  color: #111e36;
}

.mk-section__tabs {
  font-size: 8px;
  color: #8b95a5;
}

.mk-mini-head {
  margin-top: 4px;
  margin-bottom: 10px;
  font-size: 10px;
  color: #8b95a5;
}

.mk-rank-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}

.mk-rank {
  display: flex;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.mk-rank__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}

.mk-rank__tag--1 {
  background-color: #fdda38;
}

.mk-rank__tag--2 {
  background-color: #c5cfdc;
}

.mk-rank__tag--3 {
  background-color: #d8bea2;
}

.mk-rank__info {
  min-width: 0;
}

.mk-rank__name {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 600;
  color: #111e36;
  white-space: nowrap;
}

.mk-rank__code {
  margin-top: 2px;
  font-size: 10px;
  color: #8b95a5;
}

.mk-rank__right {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 20px;
  text-align: right;
}

.mk-rank__value {
  font-size: 12px;
  white-space: nowrap;
}

.mk-rank__pct {
  font-size: 12px;
}

.mk-radar-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.mk-radar-name {
  font-size: 13px;
  font-weight: 600;
  color: #111e36;
}

.mk-radar-code {
  margin-top: 2px;
  font-size: 10px;
  color: #8b95a5;
}

.mk-radar-right {
  text-align: right;
}

.mk-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background-color: #e51a2d;
  border-radius: 12px;
}

.mk-radar-vol {
  margin-top: 4px;
  font-size: 11px;
  color: #8b95a5;
}

.mk-sector-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
}

.mk-sector-dot {
  width: 3px;
  height: 14px;
  margin-right: 8px;
  background-color: #e51a2d;
  border-radius: 2px;
}

.mk-sector-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #111e36;
}

.mk-sector-bar {
  width: 14px;
  height: 14px;
  background-color: #e51a2d;
  border-radius: 3px;
}

.mk :deep(.card-conten) {
  min-height: auto;
  border-radius: 10px;
  box-shadow: 0 4px 8px 0 rgb(191 206 229 / 50%);
}

.mk :deep(.card-conten .header) {
  height: 44px;
  padding: 0 14px;
}

.mk :deep(.card-conten .header .title) {
  font-size: 18px;
  font-weight: 700;
  color: #111e36;
}
</style>
